import { _generateIntegerHashForString } from "@/modules/string/generateIntegerHash";

type CompactStringForLoggingOptions = {
  maxLength?: number;
  suffixLength?: number;
};

export type CompactStringForLoggingResult = {
  hash: number;
  length: number;
  preview: string;
  truncated: boolean;
};

const DEFAULT_MAX_LENGTH = 200;
const DEFAULT_TITLE_MAX_LENGTH = 160;
const DEFAULT_SUFFIX_LENGTH = 24;

const normalizeMaxLength = (maxLength: number | undefined, fallback: number) => {
  if (typeof maxLength !== "number" || !Number.isFinite(maxLength)) return fallback;
  return Math.max(0, Math.floor(maxLength));
};

const compactPreview = ({
  value,
  maxLength,
  suffixLength,
}: {
  value: string;
  maxLength: number;
  suffixLength: number;
}) => {
  if (value.length <= maxLength) return value;
  if (maxLength <= 3) return value.slice(0, maxLength);

  const normalizedSuffixLength = Math.min(
    Math.max(0, suffixLength),
    Math.max(0, Math.ceil((maxLength - 3) / 2))
  );
  const prefixLength = Math.max(0, maxLength - normalizedSuffixLength - 3);

  if (normalizedSuffixLength === 0) {
    return `${value.slice(0, maxLength - 3)}...`;
  }

  return `${value.slice(0, prefixLength)}...${value.slice(value.length - normalizedSuffixLength)}`;
};

export const _compactStringForLogging = (
  value: string,
  options: CompactStringForLoggingOptions = {}
): CompactStringForLoggingResult => {
  const maxLength = normalizeMaxLength(options.maxLength, DEFAULT_MAX_LENGTH);
  const suffixLength = normalizeMaxLength(options.suffixLength, DEFAULT_SUFFIX_LENGTH);

  return {
    hash: _generateIntegerHashForString({ str: value }),
    length: value.length,
    preview: compactPreview({ value, maxLength, suffixLength }),
    truncated: value.length > maxLength,
  };
};

export const _compactTitleForLogging = (
  value: string,
  options: CompactStringForLoggingOptions = {}
): CompactStringForLoggingResult => {
  return _compactStringForLogging(value, {
    maxLength: options.maxLength ?? DEFAULT_TITLE_MAX_LENGTH,
    suffixLength: options.suffixLength ?? DEFAULT_SUFFIX_LENGTH,
  });
};
